
import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { User } from '../types';
import { X, Lock, User as UserIcon, Mail, Loader2, ArrowRight, ShieldCheck, RefreshCw, Key, Info, CheckCircle2 } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

type Mode = 'login' | 'register' | 'forgot' | 'reset_confirm' | 'verify_info';

// 允许的知名邮箱列表
const ALLOWED_EMAIL_DOMAINS = [
  'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 
  'icloud.com', 'proton.me', 'qq.com', '163.com', '126.com', 'sina.com'
];

const AuthModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState<Mode>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, answer: '' });
  const refreshCaptcha = () => setCaptcha({ a: Math.floor(Math.random() * 20), b: Math.floor(Math.random() * 20), answer: '' });

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    token: ''
  });

  useEffect(() => {
    if (mode === 'register') refreshCaptcha();
    setError('');
    setSuccess('');
  }, [mode]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  if (!isOpen) return null;

  const handleResend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    const target = formData.email || formData.username;
    const res = await authService.resendCode(target);
    if (res.success) {
      setSuccess('新的激活码已发送至您的邮箱');
      setCooldown(60);
    } else {
      setError(res.message);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'login') {
        const res = await authService.login(formData.username, formData.password);
        if (res.success) {
          onSuccess(res.user);
          onClose();
        } else {
          setError(res.message);
          if (res.needs_verification) {
            setFormData(prev => ({ ...prev, email: res.email }));
            setMode('verify_info');
          }
        }
      } else if (mode === 'register') {
        // 长度校验
        if (formData.username.length < 6 || formData.username.length > 9) {
          setError('注册账号要求 6-9 位字符');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6 || formData.password.length > 18) {
          setError('密码要求 6-18 位字符');
          setLoading(false);
          return;
        }

        // 新增：邮箱域名校验
        const emailDomain = formData.email.split('@')[1]?.toLowerCase();
        if (!ALLOWED_EMAIL_DOMAINS.includes(emailDomain)) {
          setError('仅支持主流邮箱（如 Gmail, Outlook, QQ, 网易等）注册');
          setLoading(false);
          return;
        }

        if (parseInt(captcha.answer) !== (captcha.a + captcha.b)) {
          setError('验证计算错误，请重试');
          setLoading(false);
          refreshCaptcha();
          return;
        }
        const res = await authService.register(formData.username, formData.email, formData.password);
        if (res.success) {
          setMode('verify_info');
          setSuccess('第一步成功！请检查收件箱输入 6 位激活码以完成注册。');
          setCooldown(60);
        } else {
          setError(res.message);
        }
      } else if (mode === 'verify_info') {
        const target = formData.username || formData.email;
        const res = await authService.verifyEmail(target, formData.token);
        if (res.success) {
          setMode('login');
          setSuccess('激活成功！您的账号现在可以正式使用了。');
          setFormData(prev => ({ ...prev, token: '' }));
        } else {
          setError(res.message);
        }
      } else if (mode === 'forgot') {
        const res = await authService.requestPasswordReset(formData.email);
        if (res.success) {
          setMode('reset_confirm');
          setSuccess('重置请求已发送，请查收邮件。');
        } else {
          setError(res.message);
        }
      } else if (mode === 'reset_confirm') {
        if (formData.password.length < 6 || formData.password.length > 18) {
          setError('新密码要求 6-18 位字符');
          setLoading(false);
          return;
        }
        const res = await authService.completePasswordReset(formData.email, formData.token, formData.password);
        if (res.success) {
          setMode('login');
          setSuccess('新密码设置成功，请重新登录。');
        } else {
          setError(res.message);
        }
      }
    } catch (err) {
      setError('服务器连接失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = () => {
    switch (mode) {
      case 'register': return { title: '账号注册', desc: '创建一个受保护的虚拟身份实验室' };
      case 'verify_info': return { title: '账号激活', desc: '账号已创建，请输入邮箱收到的激活码' };
      case 'forgot': return { title: '找回密码', desc: '我们将发送验证码至您的邮箱' };
      case 'reset_confirm': return { title: '重置密码', desc: '请输入激活码和您的新密码' };
      default: return { title: '会员登录', desc: '访问您的专业身份生成工作区' };
    }
  };

  const header = renderHeader();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 border border-slate-100">
        <div className="p-8">
          <button onClick={onClose} className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"><X size={20} /></button>

          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg transition-colors ${mode === 'verify_info' ? 'bg-orange-500 text-white' : 'bg-blue-600 text-white'}`}>
              {mode === 'verify_info' ? <Key size={32} /> : <ShieldCheck size={32} />}
            </div>
            <h2 className="text-2xl font-black text-slate-900">{header.title}</h2>
            <p className="text-slate-400 text-sm mt-1">{header.desc}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {(mode === 'login' || mode === 'register') && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">用户名 {mode === 'register' && '(6-9位)'}</label>
                <div className="relative">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="text" 
                    required 
                    minLength={mode === 'register' ? 6 : 1}
                    maxLength={9}
                    placeholder="输入您的用户名" 
                    value={formData.username} 
                    onChange={e => setFormData({...formData, username: e.target.value})} 
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 transition-all font-semibold" 
                  />
                </div>
              </div>
            )}

            {(mode === 'register' || mode === 'forgot' || mode === 'reset_confirm') && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">电子邮箱 {mode === 'register' && '(仅限主流服务商)'}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input type="email" required placeholder="example@gmail.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 transition-all font-semibold" />
                </div>
                {mode === 'register' && <p className="text-[9px] text-slate-400 mt-1 px-1">支持：Gmail, Outlook, QQ, 网易, Yahoo, iCloud 等</p>}
              </div>
            )}

            {(mode === 'login' || mode === 'register' || mode === 'reset_confirm') && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">{mode === 'reset_confirm' ? '新密码' : '登录密码'} {mode === 'register' && '(6-18位)'}</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input 
                    type="password" 
                    required 
                    minLength={mode === 'register' ? 6 : 1}
                    maxLength={18}
                    placeholder="设置强密码" 
                    value={formData.password} 
                    onChange={e => setFormData({...formData, password: e.target.value})} 
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 transition-all font-semibold" 
                  />
                </div>
              </div>
            )}

            {(mode === 'reset_confirm' || mode === 'verify_info') && (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">激活验证码</label>
                  <button type="button" onClick={handleResend} disabled={cooldown > 0 || loading} className="text-[10px] font-black text-blue-600 hover:text-blue-700 disabled:text-slate-300 uppercase tracking-widest">
                    {cooldown > 0 ? `${cooldown}s 后重发` : '重新发送'}
                  </button>
                </div>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input type="text" required maxLength={6} placeholder="输入 6 位验证码" value={formData.token} onChange={e => setFormData({...formData, token: e.target.value.toUpperCase()})} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 transition-all font-mono font-black text-lg tracking-widest" />
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">人机验证</label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-slate-100 rounded-2xl flex items-center justify-center font-black text-slate-600 border border-slate-200 shadow-inner">
                    {captcha.a} + {captcha.b} = ?
                  </div>
                  <input type="text" required placeholder="结果" value={captcha.answer} onChange={e => setCaptcha({...captcha, answer: e.target.value})} className="w-24 px-4 py-4 bg-white border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 font-bold text-center" />
                  <button type="button" onClick={refreshCaptcha} className="p-4 bg-slate-50 rounded-2xl text-slate-400 hover:text-blue-600 border border-slate-100 transition-all"><RefreshCw size={18} /></button>
                </div>
              </div>
            )}

            {error && <div className="text-xs font-bold p-3 rounded-xl bg-red-50 text-red-600 border border-red-100 flex items-center gap-2"><Info size={14} /> {error}</div>}
            {success && <div className="text-xs font-bold p-3 rounded-xl bg-green-50 text-green-600 border border-green-100 flex items-center gap-2"><CheckCircle2 size={14} /> {success}</div>}

            <button disabled={loading} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-slate-800 transition-all shadow-xl active:scale-[0.98] disabled:opacity-50">
              {loading ? <Loader2 size={20} className="animate-spin" /> : (mode === 'register' ? '发起注册' : '提交确认')}
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>

          <div className="mt-8 flex flex-col gap-3 text-center border-t border-slate-50 pt-6">
            {mode === 'login' ? (
              <>
                <button onClick={() => setMode('register')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">没有账号？注册新身份</button>
                <button onClick={() => setMode('forgot')} className="text-xs font-medium text-slate-400 hover:text-slate-600">忘记密码</button>
              </>
            ) : (
              <button onClick={() => setMode('login')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">返回会员登录</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
